import PDQ from "../../constants/pdq";
import { tryCatch } from "../../helpers/middleware";
import {
    coverflowSwiperOptions,
    alternateVerticalSwiperOptions,
} from "../lux-swiper/options";
import { activateClasses } from "../../helpers/dom-manipulation/dom-manipulation";
import { CountdownTimer, getParsedValue } from "../../helpers/time";
import createPdqApi from "./api";

/**
 * Product Discovery Queue (PDQ) — manages the swipeable product discovery modal
 * with vertical accept/reject gestures and a countdown timer.
 */
export class LuxPDQ {
    /**
     * Create a new LuxPDQ instance.
     *
     * @param {string} swiperIdHorizontal - DOM id of the horizontal swiper container.
     * @param {string} swiperIdVertical - Base DOM id prefix for vertical swiper containers.
     * @param {number} count - Number of vertical swipers to initialise.
     * @param {string} csrf - CSRF token for API requests.
     * @param {boolean} hasIntroductoryImage - Whether the PDQ has an introductory image slide.
     * @param {string} temporaryText - Disclaimer text to append to member price labels.
     */
    constructor(
        swiperIdHorizontal,
        swiperIdVertical,
        count,
        csrf,
        hasIntroductoryImage,
        temporaryText,
    ) {
        this.api = createPdqApi(csrf);
        this.swiperIdHorizontal = swiperIdHorizontal;

        this.swiperIdVertical = [...Array(count).keys()].map(
            (i) => swiperIdVertical + (i + 1),
        );

        /** @type {InstanceType<typeof import("../lux-swiper/LuxSwiper").swiperSlider> | null} */
        this.pdqSliderHorizontal = null;
        /** @type {Array<InstanceType<typeof import("../lux-swiper/LuxSwiper").swiperScroll>> | null} */
        this.pdqSliderVertical = null;
        /** @type {number} */
        this.translate = 0;
        this.temporaryText = temporaryText;
        this.hasIntroductoryImage = hasIntroductoryImage;
        /** @type {Element | null} */
        this.modal = null;
        /** @type {HTMLElement | null} */
        this.modalId = null;
        /** @type {Element | null} */
        this.modalButton = null;
        /** @type {Element | null} */
        this.swiperWrapper = null;
        /** @type {Element | null} */
        this.countdownDaysField = null;
        /** @type {Element | null} */
        this.countdownHoursField = null;
        /** @type {Element | null} */
        this.countdownMinutesField = null;
        /** @type {Element | null} */
        this.countdownSecondsField = null;
        /** @type {CountdownTimer | null} */
        this.countdownTimer = null;
        /** @type {{ days: number, hours: number, minutes: number, seconds: number } | null} */
        this.totalSecondsConfig = null;
        this.initialDays = 0;
        this.initialHours = 0;
        this.initialMinutes = 0;
        this.initialSeconds = 0;
    }

    /**
     * Initialise the PDQ — query DOM elements, start countdown, attach modal button.
     *
     * @returns {void}
     */
    init() {
        this.modal = document.querySelector(PDQ.modal);
        this.modalId = document.getElementById(PDQ.modalId);
        this.modalButton = document.querySelector(PDQ.modalButton);
        this.swiperWrapper = document.querySelector(PDQ.swiperWrapper);
        this.countdownDaysField = document.querySelector(PDQ.countdown.days);
        this.countdownHoursField = document.querySelector(PDQ.countdown.hours);
        this.countdownMinutesField = document.querySelector(
            PDQ.countdown.minutes,
        );
        this.countdownSecondsField = document.querySelector(
            PDQ.countdown.seconds,
        );

        if (!this.modal || !this.swiperWrapper) return;

        this._initCountdown();
        this._attachModalButton();
    }

    /**
     * Read initial countdown field values and start the countdown timer.
     *
     * @returns {void}
     */
    _initCountdown() {
        this.initialDays = getParsedValue(
            /** @type {HTMLElement | null} */ (this.countdownDaysField),
        );
        this.initialHours = getParsedValue(
            /** @type {HTMLElement | null} */ (this.countdownHoursField),
        );
        this.initialMinutes = getParsedValue(
            /** @type {HTMLElement | null} */ (this.countdownMinutesField),
        );
        this.initialSeconds = getParsedValue(
            /** @type {HTMLElement | null} */ (this.countdownSecondsField),
        );

        this.totalSecondsConfig = {
            days: this.initialDays,
            hours: this.initialHours,
            minutes: this.initialMinutes,
            seconds: this.initialSeconds,
        };

        this.countdownTimer = new CountdownTimer(this.totalSecondsConfig, {
            days: /** @type {HTMLElement | undefined} */ (
                this.countdownDaysField ?? undefined
            ),
            hours: /** @type {HTMLElement} */ (this.countdownHoursField),
            minutes: /** @type {HTMLElement} */ (this.countdownMinutesField),
            seconds: /** @type {HTMLElement | undefined} */ (
                this.countdownSecondsField ?? undefined
            ),
        });

        this.countdownTimer.start();
    }

    /**
     * Attach a click listener to the modal button that initialises the PDQ slider.
     *
     * @returns {void}
     */
    _attachModalButton() {
        if (!this.modalButton) return;

        this.modalButton.addEventListener("click", async () => {
            await tryCatch(async () => await this.initPDQSlider())();

            setTimeout(
                () => activateClasses(PDQ.modalId, [".left", ".right"]),
                300,
            );
            setTimeout(
                () => activateClasses(PDQ.modalId, [".down", ".up"]),
                800,
            );
        });
    }

    /**
     * Initialise the horizontal and vertical PDQ swipers, or restore translate on re-open.
     *
     * @returns {Promise<void>}
     */
    async initPDQSlider() {
        if (!this.pdqSliderHorizontal) {
            this.pdqSliderHorizontal = new swiperSlider(
                this.swiperIdHorizontal,
                coverflowSwiperOptions,
            );

            const swiper = this.pdqSliderHorizontal._swiper;
            if (!swiper) return;

            const swiperWrapperEl = this.swiperWrapper;
            if (!swiperWrapperEl) return;

            const [firstEl, secondEl] = [
                swiperWrapperEl.querySelector(PDQ.firstIntroductorySlide),
                swiperWrapperEl.querySelector(PDQ.secondIntroductorySlide),
            ];

            const slides = Array.from(
                swiperWrapperEl.querySelectorAll(PDQ.luxPriceMember),
            );
            slides.forEach((slide) => {
                const span = document.createElement("span");
                span.classList.add("lux-price-member-disclaimer");
                span.textContent = this.temporaryText;
                slide.appendChild(span);
            });

            const modalIdEl = this.modalId;
            const hasIntro = this.hasIntroductoryImage;

            const handleSlideChange = () => {
                const indexThreshold = hasIntro ? 2 : 1;
                const isFirstTwo = swiper.activeIndex < indexThreshold;

                if (modalIdEl) {
                    modalIdEl.classList.toggle(
                        PDQ.introductorySlide,
                        isFirstTwo,
                    );
                }

                [firstEl, secondEl].forEach((el) => {
                    if (el)
                        el.classList.toggle(
                            PDQ.introductorySlideOverlay,
                            !isFirstTwo,
                        );
                });
            };

            swiper.on("slideChange", handleSlideChange);
            swiper.on("reachBeginning", handleSlideChange);
        } else {
            const slider = /** @type {any} */ (this.pdqSliderHorizontal);
            if (typeof slider.updateTranslate === "function") {
                slider.updateTranslate(this.translate);
            }
        }

        if (!this.pdqSliderVertical) {
            this.pdqSliderVertical = this.swiperIdVertical.map((id) =>
                this._initVerticalSwiper(id),
            );
        }
    }

    /**
     * Initialise a single vertical swiper for accept/reject gestures.
     *
     * @param {string} id - The DOM id of the vertical swiper container.
     * @returns {InstanceType<typeof import("../lux-swiper/LuxSwiper").swiperScroll>} The initialised vertical swiper.
     */
    _initVerticalSwiper(id) {
        const verticalSlider = new swiperScroll(
            id,
            alternateVerticalSwiperOptions,
        );

        const swipeTriggered = { up: false, down: false };
        const self = this;

        const verticalSwiper = verticalSlider._swiper;
        if (verticalSwiper) {
            verticalSwiper.on(
                "setTranslate",
                /**
                 * Handle vertical swipe gestures to trigger accept/reject actions.
                 *
                 * @param {import("swiper/bundle").default} swiperInstance - The Swiper instance that fired the event.
                 */
                function (swiperInstance) {
                    const activeOuterSlide = swiperInstance.el.closest(
                        PDQ.slider.outer,
                    );
                    const thresholdUp = PDQ.values.TRANSLATE_THRESHOLD_UP;
                    const thresholdDown = PDQ.values.TRANSLATE_THRESHOLD_DOWN;

                    if (
                        swiperInstance.translate < thresholdUp &&
                        !swipeTriggered.up
                    ) {
                        if (!activeOuterSlide) return;
                        self.handleAction(
                            swiperInstance,
                            PDQ.arrow.up,
                            activeOuterSlide,
                        );
                        swipeTriggered.up = true;
                    } else if (swiperInstance.translate >= thresholdUp)
                        swipeTriggered.up = false;

                    if (
                        swiperInstance.translate > thresholdDown &&
                        !swipeTriggered.down
                    ) {
                        if (!activeOuterSlide) return;
                        self.handleAction(
                            swiperInstance,
                            PDQ.arrow.down,
                            activeOuterSlide,
                        );
                        swipeTriggered.down = true;
                    } else if (swiperInstance.translate <= thresholdDown)
                        swipeTriggered.down = false;
                },
            );
        }

        const sliderElement = document.getElementById(id);
        if (sliderElement) {
            sliderElement.addEventListener("click", (e) => {
                const arrow = /** @type {HTMLElement} */ (e.target).closest(
                    PDQ.arrow.base,
                );
                if (!arrow) return;

                e.preventDefault();
                const outerSlide = arrow.closest(PDQ.slider.outer);

                if (arrow.matches(PDQ.arrow.up)) {
                    if (
                        outerSlide &&
                        outerSlide.matches(PDQ.activeOuterSlide)
                    ) {
                        self.handleAction(
                            verticalSlider._swiper
                                ? verticalSlider._swiper
                                : /** @type {any} */ (null),
                            PDQ.arrow.up,
                            outerSlide,
                        );
                    }
                } else if (arrow.matches(PDQ.arrow.down)) {
                    self.handleAction(
                        verticalSlider._swiper
                            ? verticalSlider._swiper
                            : /** @type {any} */ (null),
                        PDQ.arrow.down,
                        outerSlide,
                    );
                }
            });
        }

        return verticalSlider;
    }

    /**
     * Handle a PDQ action (add or remove product) triggered by swipe or arrow click.
     *
     * @param {any} swiperInstance - The Swiper instance that triggered the action.
     * @param {string} selector - The action selector (arrow up or arrow down).
     * @param {Element | null} [outerSlide=null] - The outer slide element being acted upon.
     * @param {number} [delay] - Delay in ms before removing the slide.
     * @returns {Promise<void>}
     */
    async handleAction(
        swiperInstance,
        selector,
        outerSlide = null,
        delay = PDQ.values.ACTION_DELAY,
    ) {
        if (!this.pdqSliderHorizontal || !this.pdqSliderHorizontal._swiper)
            return;
        const activeIndex = this.pdqSliderHorizontal._swiper.activeIndex;
        if (
            !outerSlide &&
            (selector === PDQ.arrow.down || selector === PDQ.arrow.up)
        )
            return;

        const productId = this.getProductIdFromSlide(outerSlide);
        if (!productId) return;

        if (selector === PDQ.arrow.down) {
            const { success } = await this.api.fetchAddProduct(productId);
            if (success && outerSlide) {
                outerSlide.classList.add("bought");
                basket.updateCartAmount(1);
            }
        } else if (selector === PDQ.arrow.up) {
            const { success } = await this.api.fetchRemoveProduct(productId);
            if (success && outerSlide) outerSlide.classList.add("removed");
        }

        const horizontalSwiper = this.pdqSliderHorizontal._swiper;
        setTimeout(() => {
            swiperInstance.destroy();
            if (horizontalSwiper) {
                horizontalSwiper.removeSlide(activeIndex);
            }
        }, delay);
    }

    /**
     * Extract the numeric product ID from a slide's inner product item element.
     *
     * @param {Element | null} slide - The outer slide element.
     * @returns {string | undefined} The extracted product ID string, or undefined.
     */
    getProductIdFromSlide(slide) {
        if (!slide) return undefined;
        const itemWrap = slide.querySelector(PDQ.itemWrap);
        const prodIdAttr = itemWrap?.getAttribute(PDQ.dataProdId);
        const match = prodIdAttr?.match(/\d+/);
        return match ? match[0] : undefined;
    }
}
